import React, { forwardRef, memo, useEffect, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import ErrMessageTooltip from '../../Errors/ErrMessageTooltip'
import { FormattedMessage } from 'react-intl'
import { IErrMsg } from '../types'
import { isValidDouble } from './utils/isValidDouble'
import { startsWithConsecutiveZeros } from './utils/startsWithConsecutiveZeros'
import { isEmptyString } from '../../../utils/isEmptyString'
import { DescriptionBtnWithPopup } from '../../buttons/PromptBtn/DescriptionBtnWithPopup'
import { RequiredMark } from '../../ui/RequiredMark/RequiredMark'
import { RiskyParamUnlockBtn } from '../components/RiskyParam/RiskyParamUnlockBtn'

export interface DoubleInputProps {
    className?: string
    state: any
    touched?: any
    errors?: any
    errorText?: string
    name: string
    label?: string
    placeholder?: string
    isDisabled?: boolean
    tooltipText?: string
    changeWarning?: string
    isColumn?: boolean
    isLabelHidden?: boolean
    precision: number | string
    isRequired?: boolean
    isFeedbackFixed?: boolean
    errMsg?: IErrMsg
    onEnter?: Function
    onBlur?: Function
    onChange?: Function

    setState(state: any): any
    setTouched?(touched: any): any
}

export const DoubleInput = memo(
    forwardRef<HTMLInputElement, DoubleInputProps>(function DoubleInput(
        {
            setState,
            setTouched,
            className,
            state,
            touched,
            errors,
            errMsg,
            name,
            label,
            precision,
            placeholder,
            isDisabled,
            tooltipText,
            changeWarning,
            isColumn,
            isLabelHidden,
            isRequired,
            isFeedbackFixed,
            onEnter,
            onBlur,
            onChange,
        },
        ref
    ) {
        const [isRiskyParam, setIsRiskyParam] = useState(!!changeWarning)
        const [showNumOnlyTooltip, setShowNumOnlyTooltip] = useState(false)
        const target = useRef(null)

        const isInvalid = touched && touched[name] && errors && errors[name]

        const handleUnlockRiskyParam = () => setIsRiskyParam(false)

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setShowNumOnlyTooltip(false)
            if (typeof onBlur === 'function') return onBlur(e)

            setTouched && setTouched({ ...touched, [name]: true })
            if (state[name] === '') {
                setState({ ...state, [name]: null })
            }
        }

        const handleChange = (event: any): void => {
            const newValue = event.target.value
            const prevValue = state[name]

            if (isValidDouble(newValue)) {
                setShowNumOnlyTooltip(false)
                setTouched && setTouched({ ...touched, [name]: true })

                if (startsWithConsecutiveZeros(newValue, prevValue)) {
                    return
                }
                if (isEmptyString(newValue)) {
                    setState({ ...state, [name]: null })
                    if (typeof onChange === 'function') onChange(null)
                    return
                }
                setState({ ...state, [name]: newValue })
                if (typeof onChange === 'function') onChange(newValue)
            } else {
                setShowNumOnlyTooltip(true)
            }
        }

        const handleOnKeyDown = (evt: any) => {
            if (evt.key === 'Enter' && typeof onEnter === 'function') {
                onEnter(evt)
            }
        }

        useEffect(() => {
            if (state?.[name] === null && isRequired) {
                setState({ ...state, [name]: '' })
                if (typeof onChange === 'function') onChange('')
            }
        }, [state?.[name]]) // eslint-disable-line

        return (
            <Form.Group
                style={{ maxWidth: '600px' }}
                className={`${className} ${
                    isInvalid && !isFeedbackFixed && 'mb-4'
                } d-flex flex-1 mt-1 mb-1 justify-content-between align-items-baseline`}
            >
                {label && !isLabelHidden ? (
                    <Form.Label
                        style={{
                            maxWidth: isColumn ? '30%' : 'auto',
                            opacity: isRiskyParam ? '50%' : '100%',
                        }}
                        className="label-main mt-1 text-capital pe-2"
                    >
                        <span className="label-main-wrapper p-relative">
                            {label}: {isRequired && <RequiredMark />}
                        </span>
                    </Form.Label>
                ) : null}
                <div
                    style={{ width: '70%' }}
                    className={`d-flex ${!isColumn ? 'flex-1' : ''}`}
                >
                    <span className="d-flex flex-column w-100 p-relative">
                        {isRiskyParam && <div className="input-overlay" />}
                        <Form.Control
                            className={`input-main ${
                                tooltipText && 'input-padding'
                            }`}
                            type="string"
                            placeholder={placeholder}
                            value={state?.[name] ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!!isDisabled}
                            isInvalid={isInvalid}
                            ref={ref ?? target}
                            name={name}
                            onKeyDown={handleOnKeyDown}
                            tabIndex={isRiskyParam ? -1 : 0}
                        />
                        <Form.Control.Feedback
                            className="input-feedback"
                            type="invalid"
                        >
                            <FormattedMessage
                                id={errMsg?.message || 'field-validation-error'}
                                values={errMsg?.values}
                                tagName="span"
                            />
                        </Form.Control.Feedback>
                        {tooltipText && (
                            <DescriptionBtnWithPopup
                                type={'default'}
                                header={label ?? ''}
                                description={tooltipText ?? ''}
                                className="mt-1 input-tooltip"
                            />
                        )}
                    </span>
                    {isRiskyParam && (
                        <RiskyParamUnlockBtn
                            onUnlockConfirm={handleUnlockRiskyParam}
                            changeWarning={changeWarning}
                        />
                    )}
                </div>
                <ErrMessageTooltip
                    message="num-only-toast"
                    error={showNumOnlyTooltip}
                    target={ref ?? target}
                />
            </Form.Group>
        )
    })
)
